import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CartesianGrid, Line, LineChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis,
  Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { DIAGNOSTIC_QUESTION_COUNT, type ExamCode } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { chartColor, useTheme } from '../lib/theme';
import { useEntitlements, useExams, useMe, useProgress, useSessions } from '../lib/queries';
import { SM_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { AccuracyBar, Callout, EmptyState, ErrorNotice, Spinner, StatCard } from '../components/ui';

export function Dashboard() {
  const { data: me } = useMe();
  const { data: exams } = useExams();
  const target = me?.user?.examTarget ?? 'CAOP';
  const available: ExamCode[] = target === 'BOTH' ? ['CAOP', 'OPRA'] : [target as ExamCode];
  const [examCode, setExamCode] = useState<ExamCode>(available[0] ?? 'CAOP');

  const exam = exams?.exams.find((e) => e.code === examCode);
  const { data: progress, isLoading } = useProgress(examCode);
  const { data: sessions } = useSessions();

  const navigate = useNavigate();
  const isWide = useMediaQuery(SM_QUERY);
  const { data: ent } = useEntitlements();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Whether this account can sit a full-length paper at all. The server
  // enforces it; this only decides which call to action to show.
  const canSitFullLength = ent?.entitlements?.fullLengthSims ?? false;

  // Charts draw to a canvas, so they cannot inherit colours from CSS. Re-read
  // the themed values whenever the resolved theme changes.
  const { resolved } = useTheme();
  const palette = useMemo(
    () => ({
      grid: chartColor('grid'),
      axis: chartColor('axis'),
      primary: chartColor('primary'),
      baseline: chartColor('baseline'),
    }),
    [resolved],
  );

  const inProgress = sessions?.sessions.find((s) => s.examCode === examCode && s.completedAt === null);

  /**
   * Starts whichever sitting this account is entitled to: the short diagnostic
   * on free, the full-length paper on a paid plan. Both land in the same
   * runner and produce the same benchmark shape, so everything downstream —
   * the radar, the report, the timetable — is indifferent to which ran.
   */
  const startSitting = async (kind: 'diagnostic' | 'baseline') => {
    setStarting(true);
    setError(null);
    try {
      const { sessionId } = await api.post<{ sessionId: string }>(`/api/sessions/${kind}`, { examCode });
      navigate(`/exam/${sessionId}`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not start the assessment.');
      setStarting(false);
    }
  };

  const startEntitledSitting = () => startSitting(canSitFullLength ? 'baseline' : 'diagnostic');

  const radarData = useMemo(
    () =>
      (progress?.domains ?? []).map((d) => ({
        domain: d.domainCode,
        name: d.domainName,
        Baseline: d.baselineAccuracyPct ?? 0,
        Latest: d.latestAccuracyPct ?? 0,
      })),
    [progress],
  );

  const trendData = useMemo(
    () =>
      (progress?.trend ?? []).map((t, i) => ({
        label: i === 0 ? 'Baseline' : `#${i + 1}`,
        date: new Date(t.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
        Accuracy: t.accuracyScore,
        Pace: t.speedScore,
      })),
    [progress],
  );

  if (isLoading) return <Spinner label="Loading your progress" />;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {me?.user?.firstName ? `Welcome back, ${me.user.firstName}` : 'Your dashboard'}
          </h1>
          <p className="mt-2 text-muted">
            {exam ? `${exam.name} — ${exam.questionCount} questions in ${exam.timeLimitSec / 60} minutes.` : null}
          </p>
        </div>
        {available.length > 1 ? (
          <div className="flex rounded-lg border border-line bg-surface p-1">
            {available.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setExamCode(code)}
                className={`min-h-11 rounded px-3.5 text-sm font-semibold transition sm:min-h-0 sm:py-1.5 ${
                  examCode === code ? 'bg-brand-700 text-white' : 'text-muted hover:bg-raised'
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {error ? <ErrorNotice message={error} /> : null}

      {inProgress ? (
        <Callout tone="warn">
          You have a {inProgress.mode} session in progress ({inProgress.answeredCount} of{' '}
          {inProgress.questionCount} answered).{' '}
          <Link to={`/exam/${inProgress.id}`} className="font-semibold underline underline-offset-2">
            Resume it
          </Link>
        </Callout>
      ) : null}

      {!progress?.hasBaseline ? (
        <EmptyState
          title={canSitFullLength ? 'Start with a full-length paper' : 'Start with a free diagnostic'}
          body={
            canSitFullLength
              ? exam
                ? `Sit the real thing: ${exam.questionCount} questions in ${exam.timeLimitSec / 60} minutes, weighted exactly like the ${exam.code} paper.`
                : 'Sit a full-length assessment to establish your baseline.'
              : `${DIAGNOSTIC_QUESTION_COUNT} questions, weighted across every ${examCode} content domain in the same proportions as the real paper. ` +
                'About half an hour, and it tells you which domains are costing you marks and whether your pace is survivable.'
          }
          action={
            <button
              type="button"
              className="btn-primary px-6 py-3 text-base"
              onClick={() => void startEntitledSitting()}
              disabled={starting}
            >
              {starting
                ? 'Preparing your paper…'
                : canSitFullLength
                  ? `Start my full ${examCode} paper`
                  : `Start my free ${examCode} diagnostic`}
            </button>
          }
        />
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Latest accuracy"
              value={progress.latest?.accuracyScore ?? 0}
              suffix="%"
              tone={(progress.latest?.accuracyScore ?? 0) >= 70 ? 'good' : (progress.latest?.accuracyScore ?? 0) >= 50 ? 'warn' : 'bad'}
              hint={
                progress.baseline && progress.latest
                  ? `${(progress.latest.accuracyScore - progress.baseline.accuracyScore >= 0 ? '+' : '')}${(progress.latest.accuracyScore - progress.baseline.accuracyScore).toFixed(1)} points since your baseline.`
                  : 'Your baseline result.'
              }
            />
            <StatCard
              label="Pace score"
              value={progress.latest?.speedScore ?? 0}
              suffix="/100"
              tone={(progress.latest?.speedScore ?? 0) >= 90 ? 'good' : 'warn'}
              hint={`Target: ${progress.benchmarkPaceSec}s per question.`}
            />
            <StatCard label="Questions answered" value={progress.totalAnswered} hint="Across every session on this exam." />
            {progress.latest?.bookRelianceScore !== null && progress.latest ? (
              <StatCard
                label="Reference reliance"
                value={progress.latest.bookRelianceScore ?? 0}
                suffix="/100"
                tone={(progress.latest.bookRelianceScore ?? 0) > 50 ? 'bad' : 'good'}
                hint="Lower is better. Self-reported, approximate."
              />
            ) : (
              <StatCard label="Benchmarks" value={progress.trend.length} hint="Re-run one any time to see movement." />
            )}
          </section>

          {/*
            The conversion moment. A free user reaches this point having just
            been shown, in their own numbers, which heavily-weighted domains are
            costing them marks — which is the one moment the upgrade is a
            genuine answer to a question they are already asking, rather than an
            interruption. It sits after the scores and before the charts for
            that reason, and disappears entirely once they are on a paid plan.
          */}
          {!canSitFullLength ? (
            <section className="card border-brand-600/40 bg-brand-50/60 p-4 dark:bg-brand-500/10 sm:p-6">
              <h2 className="text-lg font-semibold text-ink">You have your diagnostic. Now rehearse the real thing.</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">
                Your {examCode} diagnostic sampled {DIAGNOSTIC_QUESTION_COUNT} questions across every content
                domain. The full paper is {exam ? `${exam.questionCount} questions in ${exam.timeLimitSec / 60} minutes` : 'considerably longer'} — long
                enough that pace and stamina start to decide the result, which is exactly what a
                diagnostic cannot measure.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted">
                {[
                  'Unlimited full-length, exam-accurate timed simulations',
                  'A written analysis of your gaps and what to do about them',
                  'A revision timetable built around your exam date and the hours you have',
                  'Unlimited practice questions',
                ].map((line) => (
                  <li key={line} className="flex gap-2.5">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                    </svg>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Link to="/pricing" className="btn-primary mt-5 w-full sm:w-auto">See plans</Link>
            </section>
          ) : null}

          <div className="grid gap-5 lg:grid-cols-2">
            {radarData.length > 0 ? (
              <section className="card p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-ink">Domain profile</h2>
                <p className="mt-1 text-sm text-muted">Latest benchmark against your baseline.</p>
                <div className="mt-4 h-64 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} outerRadius={isWide ? '72%' : '62%'}>
                      <PolarGrid stroke={palette.grid} />
                      <PolarAngleAxis dataKey="domain" tick={{ fontSize: isWide ? 12 : 11, fill: palette.axis }} />
                      {/* Radius ticks collide with the web itself once the plot
                          is under ~300px wide; the grid rings still carry the
                          scale, and the tooltip carries the exact figure. */}
                      {isWide ? (
                        <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: palette.baseline }} />
                      ) : (
                        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                      )}
                      <Radar name="Baseline" dataKey="Baseline" stroke={palette.baseline} fill={palette.baseline} fillOpacity={0.2} />
                      <Radar name="Latest" dataKey="Latest" stroke={palette.primary} fill={palette.primary} fillOpacity={0.35} />
                      <Tooltip formatter={(v: number) => `${v}%`} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                {/* Touch has no hover, so the series need naming outside the plot. */}
                <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                  <li className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: palette.baseline }} aria-hidden="true" />
                    Baseline
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: palette.primary }} aria-hidden="true" />
                    Latest
                  </li>
                </ul>
              </section>
            ) : null}

            {trendData.length > 1 ? (
              <section className="card p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-ink">Trend</h2>
                <p className="mt-1 text-sm text-muted">Accuracy and pace across every benchmark.</p>
                <div className="mt-4 h-64 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    {/* No negative left margin: it shifts the axis out of the
                        SVG and clips the tick labels to their last digit. */}
                    <LineChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: isWide ? 12 : 10, fill: palette.axis }}
                        {...(isWide ? {} : { interval: 'preserveStartEnd' as const, minTickGap: 16 })}
                      />
                      <YAxis
                        domain={[0, 100]}
                        width={isWide ? 60 : 40}
                        tick={{ fontSize: isWide ? 12 : 10, fill: palette.axis }}
                      />
                      <Tooltip />
                      <Line type="monotone" dataKey="Accuracy" stroke={palette.primary} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Pace" stroke={palette.baseline} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                  <li className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: palette.primary }} aria-hidden="true" />
                    Accuracy
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ background: palette.baseline }} aria-hidden="true" />
                    Pace
                  </li>
                </ul>
              </section>
            ) : (
              <section className="card flex flex-col justify-center p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-ink">Trend</h2>
                <p className="mt-2 text-sm text-muted">
                  Re-run a full benchmark after a few weeks of practice and your trend lines will appear
                  here — movement per domain is a far better signal than a single headline number.
                </p>
                {canSitFullLength ? (
                  <button type="button" className="btn-secondary mt-4 self-start" onClick={() => void startSitting('baseline')} disabled={starting}>
                    {starting ? 'Preparing…' : 'Re-run a full benchmark'}
                  </button>
                ) : (
                  <Link to="/pricing" className="btn-secondary mt-4 self-start">
                    Unlock full-length papers
                  </Link>
                )}
              </section>
            )}
          </div>

          <section className="card p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-ink">Lifetime accuracy by domain</h2>
            <p className="mt-1 text-sm text-muted">
              Every question you have answered on this exam, weighted against the official blueprint.
            </p>
            <ul className="mt-5 space-y-4">
              {progress.domains.map((d) => (
                <li key={d.domainCode}>
                  <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-ink">
                      <span className="text-muted">{d.domainCode}</span> {d.domainName}
                    </span>
                    <span className="text-xs text-muted">
                      {d.lifetimeAttempted} answered
                      {d.deltaPct !== null ? (
                        <span className={d.deltaPct >= 0 ? ' text-emerald-600 dark:text-emerald-400' : ' text-red-600'}>
                          {' '}· {d.deltaPct >= 0 ? '+' : ''}{d.deltaPct} vs baseline
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <AccuracyBar pct={d.lifetimeAccuracyPct} weightPct={d.weightPct} />
                  <div className="mt-1.5">
                    <Link
                      to={`/practice?exam=${examCode}&domain=${encodeURIComponent(d.domainCode)}`}
                      className="text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
                    >
                      Practise this domain →
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {progress.cognitiveLevels.length > 0 ? (
            <section className="card p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-ink">Cognitive level</h2>
              <p className="mt-1 text-sm text-muted">
                OPRA samples roughly 55% recall, 30% understanding and 15% application. Knowing which of
                those is letting you down tells you whether to read more or practise more.
              </p>
              <ul className="mt-5 space-y-4">
                {progress.cognitiveLevels.map((c) => (
                  <li key={c.name}>
                    <div className="mb-1.5 flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-ink">{c.name}</span>
                      <span className="text-xs text-muted">{c.correct}/{c.attempted}</span>
                    </div>
                    <AccuracyBar pct={c.accuracyPct} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {progress.topicStrengths.length > 0 ? (
            <section className="card p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-ink">Weakest topics</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {progress.topicStrengths.map((t) => (
                  <li key={t.tagName} className="flex items-center justify-between gap-3 rounded-lg border border-line p-3">
                    <span className="text-sm text-muted">{t.tagName}</span>
                    <span className="text-sm font-semibold tabular-nums text-ink">{t.accuracyPct}%</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="flex flex-wrap gap-3">
            <Link to="/practice" className="btn-primary">Start a practice set</Link>
            <Link to="/revision" className="btn-secondary">My revision timetable</Link>
            {canSitFullLength ? (
              <button type="button" className="btn-secondary" onClick={() => void startSitting('baseline')} disabled={starting}>
                Re-run full benchmark
              </button>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}
